import { useEffect, useRef, useState } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop, type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ImagePreprocessingError,
  preprocessImageToWebP,
  type PreparedImage,
} from '@/lib/image-preprocessing';

import { ImageCropCoordinates } from './ImageCropCoordinates';

import type { ImageProcessingPolicy } from './image-processing-policy';

interface ImageCropDialogProps {
  file: File | null;
  open: boolean;
  policy: ImageProcessingPolicy;
  onOpenChange: (open: boolean) => void;
  onPrepared: (preparedImage: PreparedImage) => void;
  closeOnPrepared?: boolean;
  applyLabel?: string;
  initialCropPercent?: number;
}

function createInitialCrop(
  image: HTMLImageElement,
  aspectRatio: number | null,
  percent: number
): Crop {
  if (aspectRatio === null) {
    return {
      unit: '%',
      x: (100 - percent) / 2,
      y: (100 - percent) / 2,
      width: percent,
      height: percent,
    };
  }

  const width = image.width || image.naturalWidth;
  const height = image.height || image.naturalHeight;

  return centerCrop(
    makeAspectCrop({ unit: '%', width: percent }, aspectRatio, width, height),
    width,
    height
  );
}

function toPixelCrop(crop: Crop, image: HTMLImageElement): PixelCrop {
  if (crop.unit === 'px') return crop as PixelCrop;

  const width = image.width || image.naturalWidth;
  const height = image.height || image.naturalHeight;

  return {
    unit: 'px',
    x: (crop.x ?? 0) * (width / 100),
    y: (crop.y ?? 0) * (height / 100),
    width: crop.width * (width / 100),
    height: crop.height * (height / 100),
  };
}

function toSourceCrop(crop: PixelCrop, image: HTMLImageElement) {
  const renderedWidth = image.width || image.naturalWidth;
  const renderedHeight = image.height || image.naturalHeight;

  if (!renderedWidth || !renderedHeight || !image.naturalWidth || !image.naturalHeight) return null;

  const scaleX = image.naturalWidth / renderedWidth;
  const scaleY = image.naturalHeight / renderedHeight;
  const sourceLeft = Math.round(crop.x * scaleX);
  const sourceTop = Math.round(crop.y * scaleY);
  const sourceRight = Math.round((crop.x + crop.width) * scaleX);
  const sourceBottom = Math.round((crop.y + crop.height) * scaleY);
  const sourceCoordinates = [sourceLeft, sourceTop, sourceRight, sourceBottom];

  if (sourceCoordinates.some((value) => !Number.isFinite(value))) return null;

  const x = clamp(sourceLeft, 0, image.naturalWidth);
  const y = clamp(sourceTop, 0, image.naturalHeight);
  const right = clamp(sourceRight, 0, image.naturalWidth);
  const bottom = clamp(sourceBottom, 0, image.naturalHeight);
  const width = right - x;
  const height = bottom - y;

  if (width <= 0 || height <= 0) return null;

  return { x, y, width, height };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function ImageCropDialog({
  file,
  open,
  policy,
  onOpenChange,
  onPrepared,
  closeOnPrepared = true,
  applyLabel = '크롭 적용',
  initialCropPercent = 90,
}: ImageCropDialogProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [cropPixels, setCropPixels] = useState<PixelCrop | null>(null);
  const [quality, setQuality] = useState(policy.defaultQuality);
  const [aspectRatio, setAspectRatio] = useState<number | null>(policy.defaultAspectRatio);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const isAspectRatioFixed = policy.aspectRatios.length === 1;

  useEffect(() => {
    if (!file || !open) return;

    const nextSourceUrl = URL.createObjectURL(file);
    setSourceUrl(nextSourceUrl);
    setCrop(undefined);
    setCropPixels(null);
    setQuality(policy.defaultQuality);
    setAspectRatio(policy.defaultAspectRatio);
    setErrorMessage(null);

    return () => URL.revokeObjectURL(nextSourceUrl);
  }, [file, open, policy.defaultAspectRatio, policy.defaultQuality]);

  const handleCropComplete = (nextCrop: PixelCrop, percentCrop?: Crop) => {
    setCropPixels(nextCrop);
    if (percentCrop) {
      setCrop(percentCrop);
      return;
    }
    const image = imageRef.current;
    if (image?.width && image.height) {
      setCrop({
        unit: '%',
        x: (nextCrop.x / image.width) * 100,
        y: (nextCrop.y / image.height) * 100,
        width: (nextCrop.width / image.width) * 100,
        height: (nextCrop.height / image.height) * 100,
      });
    }
  };

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const initialCrop = createInitialCrop(event.currentTarget, aspectRatio, initialCropPercent);
    setCrop(initialCrop);
    setCropPixels(toPixelCrop(initialCrop, event.currentTarget));
  };

  const handleAspectRatioChange = (value: string) => {
    const nextAspectRatio = value === 'free' ? null : Number(value);
    setAspectRatio(nextAspectRatio);
    setCropPixels(null);

    if (imageRef.current) {
      const initialCrop = createInitialCrop(imageRef.current, nextAspectRatio, initialCropPercent);
      setCrop(initialCrop);
      setCropPixels(toPixelCrop(initialCrop, imageRef.current));
    }
  };

  const handleApply = async () => {
    const image = imageRef.current;
    if (!file || !image || !cropPixels) {
      setErrorMessage('크롭 영역을 준비하지 못했습니다. 이미지를 다시 선택해주세요.');
      return;
    }

    const sourceCrop = toSourceCrop(crop ? toPixelCrop(crop, image) : cropPixels, image);
    if (!sourceCrop) {
      setErrorMessage('크롭 영역이 이미지 범위를 벗어났습니다. 영역을 다시 조절해주세요.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const preparedImage = await preprocessImageToWebP(file, {
        crop: sourceCrop,
        quality,
        allowedMimeTypes: policy.allowedMimeTypes,
        maxInputBytes: policy.maxInputBytes,
        maxOutputBytes: policy.maxOutputBytes,
        maxOutputLongEdge: policy.maxOutputLongEdge,
      });

      onPrepared(preparedImage);
      if (closeOnPrepared) onOpenChange(false);
    } catch (error) {
      setErrorMessage(
        error instanceof ImagePreprocessingError
          ? error.message
          : '이미지를 처리하지 못했습니다. 다른 이미지를 선택해주세요.'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const sourceCrop =
    crop && imageRef.current
      ? toSourceCrop(toPixelCrop(crop, imageRef.current), imageRef.current)
      : null;
  const cropSizeLabel = sourceCrop ? `크롭 ${sourceCrop.width} × ${sourceCrop.height}px` : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isProcessing) onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>이미지 크롭 및 변환</DialogTitle>
          <DialogDescription>
            {isAspectRatioFixed
              ? `${policy.aspectRatios[0]?.label} 비율에 맞춰 크롭 영역을 조절하세요.`
              : '자유 비율 또는 고정 비율을 선택한 뒤, 크롭 영역을 드래그하거나 모서리 핸들로 크기를 조절하세요.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex min-h-80 items-center justify-center overflow-auto rounded-md bg-muted p-3 sm:min-h-96">
            {sourceUrl && (
              <ReactCrop
                crop={crop}
                aspect={aspectRatio ?? undefined}
                keepSelection
                ruleOfThirds
                disabled={isProcessing}
                onChange={(pixelCrop, percentCrop) => {
                  setCrop(percentCrop);
                  setCropPixels(pixelCrop);
                }}
                onComplete={handleCropComplete}
                renderSelectionAddon={() =>
                  cropSizeLabel ? (
                    <span
                      className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/75 px-1.5 py-0.5 text-xs font-medium text-white"
                      aria-label={`원본 기준 ${cropSizeLabel}`}
                    >
                      {cropSizeLabel}
                    </span>
                  ) : null
                }
              >
                <img
                  ref={imageRef}
                  src={sourceUrl}
                  alt="크롭할 원본 이미지"
                  className="max-h-[24rem] w-full object-contain"
                  onLoad={handleImageLoad}
                />
              </ReactCrop>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {!isAspectRatioFixed && (
              <label className="space-y-2 text-sm font-medium">
                <span>크롭 비율</span>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  value={aspectRatio === null ? 'free' : String(aspectRatio)}
                  disabled={isProcessing}
                  onChange={(event) => handleAspectRatioChange(event.target.value)}
                >
                  {policy.aspectRatios.map((option) => (
                    <option
                      key={option.label}
                      value={option.value === null ? 'free' : option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="space-y-2 text-sm font-medium">
              <span>WebP 품질: {quality}%</span>
              <input
                className="w-full accent-primary"
                type="range"
                min="40"
                max="95"
                step="5"
                value={quality}
                disabled={isProcessing}
                onChange={(event) => setQuality(Number(event.target.value))}
              />
              <span className="block text-xs font-normal text-muted-foreground">
                변환 후 실제 해상도와 파일 크기는 적용 결과에서 확인할 수 있습니다.
              </span>
            </label>
          </div>

          {sourceCrop && imageRef.current && (
            <ImageCropCoordinates
              crop={sourceCrop}
              imageWidth={imageRef.current.naturalWidth}
              imageHeight={imageRef.current.naturalHeight}
              aspectRatio={aspectRatio}
              disabled={isProcessing}
              onApply={(coordinates) => {
                const image = imageRef.current;
                if (!image) return;
                const nextCrop: Crop = {
                  unit: '%',
                  x: (coordinates.x / image.naturalWidth) * 100,
                  y: (coordinates.y / image.naturalHeight) * 100,
                  width: (coordinates.width / image.naturalWidth) * 100,
                  height: (coordinates.height / image.naturalHeight) * 100,
                };
                setCrop(nextCrop);
                setCropPixels(toPixelCrop(nextCrop, image));
                setErrorMessage(null);
              }}
            />
          )}
          {errorMessage && (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isProcessing}
            onClick={() => onOpenChange(false)}
          >
            취소
          </Button>
          <Button
            type="button"
            disabled={isProcessing || !cropPixels}
            onClick={() => void handleApply()}
          >
            {isProcessing ? '변환 중...' : applyLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
